import React, { useEffect, useState } from "react";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import PageHeader from "../PageHeader";
import { triggerAlert } from "../../utils/commonFunctions";
import Loader from "../../common/Loader";
import { AdminTerms } from "../../utils/APIClient";

/**
 * Settings > Terms & Conditions
 *
 * One big textarea for the current T&C body. On save:
 *   - If the text is unchanged (whitespace-normalized), the backend
 *     treats it as a no-op — no version bump, no customer re-prompt.
 *   - If the text changed, the version bumps and every customer sees
 *     the popup again on their next dashboard entry.
 *
 * The customer popup renders this text with white-space: pre-wrap, so
 * single newlines (Enter) and double newlines (blank line between
 * paragraphs) carry through verbatim to what the customer reads.
 */
export default function TermsAndConditions() {
  document.title = "Terms & Conditions";
  const mainHeading = "Terms & Conditions";
  const heading = "Settings / Terms & Conditions";

  const [content, setContent] = useState("");
  const [initialContent, setInitialContent] = useState("");
  const [version, setVersion] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedByEmail, setUpdatedByEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    AdminTerms.get()
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setContent(data.content || "");
        setInitialContent(data.content || "");
        setVersion(data.version ?? null);
        setUpdatedAt(data.updated_at || null);
        setUpdatedByEmail(data.updated_by_email || null);
      })
      .catch(() => {
        triggerAlert("error", "Oops...", "Failed to load Terms & Conditions.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty = (content || "").trim() !== (initialContent || "").trim();

  const handleSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const res = await AdminTerms.update(content);
      const data = res.data || {};
      setInitialContent(content);
      setVersion(data.version ?? version);
      setUpdatedAt(data.updated_at || null);
      setUpdatedByEmail(data.updated_by_email || null);
      if (data.version_bumped) {
        triggerAlert(
          "success",
          "",
          `Saved as version ${data.version}. Every customer will be prompted to accept the new Terms on their next dashboard entry.`
        );
      } else {
        triggerAlert("info", "", "No changes detected — no customer will be re-prompted.");
      }
    } catch (err) {
      triggerAlert("error", "Oops...", "Failed to save Terms & Conditions.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setContent(initialContent);
  };

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="page-content" style={{ backgroundColor: "#ededed" }}>
        <div className="main-content">
          <PageHeader mainHeading={mainHeading} heading={heading} />
          <div className="container-fluid">
            {loading ? (
              <div className="loader-overlay text-white">
                <Loader />
              </div>
            ) : (
              <form onSubmit={handleSave}>
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h5 className="mb-1">
                          <i className="bi bi-file-earmark-text me-2" />
                          Terms & Conditions
                        </h5>
                        <small className="text-muted">
                          The text below is shown in the customer portal
                          popup. Line breaks are preserved as-is — a single
                          Enter makes a new line, a blank line makes a
                          paragraph gap. No HTML or markdown formatting.
                        </small>
                      </div>
                      <div className="text-end">
                        {version !== null && (
                          <span className="badge bg-primary">Version {version}</span>
                        )}
                        {updatedAt && (
                          <div className="text-muted small mt-1">
                            Last updated{" "}
                            {new Date(updatedAt).toLocaleString()}
                            {updatedByEmail ? ` by ${updatedByEmail}` : ""}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Content
                      </label>
                      <textarea
                        className="form-control"
                        style={{
                          minHeight: 420,
                          fontFamily:
                            "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
                          fontSize: "0.9rem",
                          lineHeight: 1.6,
                        }}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Type your Terms & Conditions here. Press Enter for a new line, blank line for a paragraph break."
                      />
                      <small className="text-muted">
                        {content.length.toLocaleString()} characters
                        {" · "}
                        {(content.match(/\n/g) || []).length + 1} line
                        {(content.match(/\n/g) || []).length + 1 === 1 ? "" : "s"}
                      </small>
                    </div>

                    <div className="alert alert-warning py-2 small mb-3">
                      <i className="bi bi-exclamation-triangle-fill me-1" />
                      Saving a real change bumps the version. <strong>All
                      customers</strong> will see the scroll-to-accept popup
                      again on their next dashboard entry. A save that
                      leaves the text unchanged is a no-op and does not
                      re-prompt anyone.
                    </div>

                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleReset}
                        disabled={!dirty || saving}
                      >
                        Reset
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Live preview so the admin sees what the customer will see. */}
                <div className="card mt-3">
                  <div className="card-body">
                    <h6 className="mb-2">
                      <i className="bi bi-eye me-2" />
                      Live preview
                    </h6>
                    <small className="text-muted d-block mb-2">
                      This is exactly how the text will render in the customer popup.
                    </small>
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        border: "1px solid #dee2e6",
                        borderRadius: 6,
                        padding: 16,
                        maxHeight: 300,
                        overflowY: "auto",
                        backgroundColor: "#fff",
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {content || (
                        <em className="text-muted">
                          (Empty — the customer popup will not appear until you
                          save non-empty content.)
                        </em>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
